import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { getTimeEntries, submitWorkUpdate } from '../../services/employeeService';
import Preloader from '../../components/common/Preloader';
import { showSuccess, showError } from '../../utils/sweetAlert';
import './EmployeeWorkUpdates.css';

const EmployeeWorkUpdates = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [dailyWorkText, setDailyWorkText] = useState('');
  const [savingWorkUpdate, setSavingWorkUpdate] = useState(false);
  const [hasSubmittedToday, setHasSubmittedToday] = useState(false);
  
  // Filters
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const loadUpdates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTimeEntries(filterMonth, filterYear);
      // Sort descending by date
      const sortedData = (data || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      setEntries(sortedData);
      
      // Find today's entry strictly matching IST
      const getIstDateString = (dateInput) => {
        const d = new Date(dateInput);
        const istString = d.toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
        const istDate = new Date(istString);
        return `${istDate.getFullYear()}-${String(istDate.getMonth()+1).padStart(2, '0')}-${String(istDate.getDate()).padStart(2, '0')}`;
      };
      
      const todayString = getIstDateString(new Date());
      const todayEntry = sortedData.find(e => getIstDateString(e.date) === todayString);
      
      if (todayEntry && todayEntry.workUpdate) {
        setHasSubmittedToday(true);
      } else {
        setHasSubmittedToday(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to load work updates');
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterYear]);

  useEffect(() => {
    loadUpdates();
  }, [loadUpdates]);

  const handleWorkUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingWorkUpdate(true);
      const today = new Date().toISOString().split('T')[0];
      await submitWorkUpdate(today, dailyWorkText);
      showSuccess('Saved!', 'Daily work update saved successfully.');
      setDailyWorkText('');
      loadUpdates();
    } catch (err) {
      showError('Failed', err.message || 'Failed to save work update');
    } finally {
      setSavingWorkUpdate(false);
    }
  };

  // Filtered entries based on Month, Year, and Custom Date / Date Range
  const filteredEntries = useMemo(() => {
    return entries.filter(e => {
      if (!e.workUpdate) return false;
      const entryD = new Date(e.date);
      const entryDateStr = `${entryD.getFullYear()}-${String(entryD.getMonth() + 1).padStart(2, '0')}-${String(entryD.getDate()).padStart(2, '0')}`;

      // If both startDate and endDate are provided (Multiple custom date range)
      if (startDate && endDate) {
        return entryDateStr >= startDate && entryDateStr <= endDate;
      }
      // If only startDate is provided
      if (startDate && !endDate) {
        return entryDateStr === startDate;
      }
      // If only endDate is provided
      if (!startDate && endDate) {
        return entryDateStr === endDate;
      }
      return true;
    });
  }, [entries, startDate, endDate]);

  // Export to Excel sheet
  const handleExportExcel = () => {
    if (filteredEntries.length === 0) {
      showError('No Data', 'No work updates available to export for the selected dates.');
      return;
    }

    try {
      const excelData = filteredEntries.map((entry, index) => {
        const d = new Date(entry.updatedAt || entry.date);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();

        const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });

        let hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        const hh = String(hours).padStart(2, '0');

        return {
          'Sl No.': index + 1,
          'Date': `${dd}-${mm}-${yyyy}`,
          'Day': dayName,
          'Submission Time': `${hh}:${minutes}:${seconds} ${ampm} IST`,
          'Work Update': entry.workUpdate || ''
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      worksheet['!cols'] = [
        { wch: 8 },  // Sl No
        { wch: 14 }, // Date
        { wch: 14 }, // Day
        { wch: 22 }, // Time
        { wch: 65 }  // Work Update
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Work Updates');

      // Generate filename based on selected dates
      let filename = `Work_Updates_${filterMonth}_${filterYear}.xlsx`;
      if (startDate && endDate) {
        filename = `Work_Updates_${startDate}_to_${endDate}.xlsx`;
      } else if (startDate) {
        filename = `Work_Updates_${startDate}.xlsx`;
      }

      XLSX.writeFile(workbook, filename);
      showSuccess('Downloaded!', `Excel sheet exported successfully (${filteredEntries.length} updates).`);
    } catch (err) {
      console.error('Export error:', err);
      showError('Export Failed', 'Unable to generate Excel file.');
    }
  };

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="work-updates-page">
      <div className="work-updates-header">
        <h1>My Work Updates</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Section 1: Submit Today's Update - Centered */}
      <div className="submit-update-card">
        <h2>Submit Today's Update</h2>
        {hasSubmittedToday ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>✅</div>
            <h3 style={{ color: '#166534', margin: '0 0 0.25rem 0', fontSize: '1.1rem' }}>Already Submitted</h3>
            <p style={{ color: '#15803d', margin: 0, fontSize: '0.9rem' }}>You have successfully submitted your daily work update for today. Great job!</p>
          </div>
        ) : (
          <form onSubmit={handleWorkUpdateSubmit} className="submit-update-form">
            <textarea
              value={dailyWorkText}
              onChange={(e) => setDailyWorkText(e.target.value)}
              placeholder="What did you work on today?"
              rows={4}
              required
              className="submit-update-textarea"
            />
            <button 
              type="submit" 
              disabled={savingWorkUpdate}
              className="btn-save-update"
            >
              {savingWorkUpdate ? 'Saving...' : 'Save Update'}
            </button>
          </form>
        )}
      </div>

      {/* Section 2: Past Updates with Responsive Layout & Excel Download */}
      <div className="past-updates-card">
        <div className="past-updates-header">
          <h2>Past Updates</h2>
          <button 
            onClick={handleExportExcel}
            className="btn-export-excel"
            disabled={filteredEntries.length === 0}
            title="Download Excel Sheet for selected dates"
          >
            📊 Download Excel Sheet ({filteredEntries.length})
          </button>
        </div>
        
        <div className="work-updates-grid">
          {/* Left Side: Responsive Filters */}
          <div className="filters-sidebar">
            <h3>Filter Updates</h3>
            
            <div className="filter-group">
              <label>Month:</label>
              <select 
                value={filterMonth} 
                onChange={(e) => { 
                  setFilterMonth(parseInt(e.target.value)); 
                  handleClearDates(); 
                }}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Year:</label>
              <select 
                value={filterYear} 
                onChange={(e) => { 
                  setFilterYear(parseInt(e.target.value)); 
                  handleClearDates(); 
                }}
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>

            <hr className="filter-divider" />
            
            {/* Multiple Custom Date Range Filters */}
            <div className="filter-group">
              <label>Multiple Custom Dates / Date Range:</label>
              <div className="date-range-row">
                <div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>From:</span>
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (e.target.value) {
                        const d = new Date(e.target.value);
                        setFilterMonth(d.getMonth() + 1);
                        setFilterYear(d.getFullYear());
                      }
                    }} 
                  />
                </div>
                <div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>To:</span>
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      if (e.target.value) {
                        const d = new Date(e.target.value);
                        setFilterMonth(d.getMonth() + 1);
                        setFilterYear(d.getFullYear());
                      }
                    }} 
                  />
                </div>
              </div>
              
              {(startDate || endDate) && (
                <button 
                  onClick={handleClearDates}
                  className="btn-clear-date"
                  style={{ marginTop: '0.5rem' }}
                >
                  Clear Custom Dates
                </button>
              )}
            </div>

            <hr className="filter-divider" />

            {/* Quick Available Dates List */}
            <div className="filter-group">
              <label>Available Dates:</label>
              {entries.filter(e => e.workUpdate).length === 0 ? (
                <div style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>No updates available.</div>
              ) : (
                <div className="available-dates-list">
                  <button 
                    onClick={handleClearDates}
                    className={`date-pill-btn ${!startDate && !endDate ? 'active' : ''}`}
                  >
                    All Dates in Month
                  </button>
                  {Array.from(new Set(entries.filter(e => e.workUpdate).map(e => new Date(e.date).toISOString().split('T')[0]))).sort((a, b) => new Date(b) - new Date(a)).map(d => {
                    const dateObj = new Date(d);
                    const isSelected = startDate === d && (!endDate || endDate === d);
                    return (
                      <button 
                        key={d}
                        onClick={() => {
                          setStartDate(d);
                          setEndDate('');
                        }}
                        className={`date-pill-btn ${isSelected ? 'active' : ''}`}
                      >
                        {dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Side: Updates List */}
          <div className="updates-feed">
            {loading ? (
              <Preloader isInline={true} />
            ) : filteredEntries.length === 0 ? (
              <div className="empty-updates-box">
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📅</div>
                <h3 style={{ color: '#334155', margin: '0 0 0.5rem 0', fontSize: '1.15rem' }}>No Work Updates Found</h3>
                <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>
                  {startDate && endDate 
                    ? `No updates found between ${new Date(startDate).toLocaleDateString()} and ${new Date(endDate).toLocaleDateString()}.`
                    : startDate 
                    ? `No updates available for ${new Date(startDate).toLocaleDateString()}.` 
                    : 'No updates available for the selected month and year.'}
                </p>
              </div>
            ) : (
              <div>
                {filteredEntries.map(entry => {
                  const d = new Date(entry.updatedAt || entry.date);
                  const dd = String(d.getDate()).padStart(2, '0');
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const yyyy = d.getFullYear();
                  
                  let hours = d.getHours();
                  const minutes = String(d.getMinutes()).padStart(2, '0');
                  const seconds = String(d.getSeconds()).padStart(2, '0');
                  const ampm = hours >= 12 ? 'PM' : 'AM';
                  hours = hours % 12 || 12;
                  const hh = String(hours).padStart(2, '0');
                  
                  const titleStr = `Daily Work Update (Date: ${dd}-${mm}-${yyyy}, Time: ${hh}:${minutes}:${seconds} ${ampm} IST)`;

                  return (
                    <div key={entry._id} className="update-entry-card">
                      <div className="update-entry-title">
                        {titleStr}
                      </div>
                      <div className="update-entry-body">
                        {entry.workUpdate}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeWorkUpdates;
